/* ============================================================
   Re-Coco Shopify Theme — Core JS
   Handles: search overlay, mobile menu, cart drawer, wishlist
   ============================================================ */

(function () {
  'use strict';

  // ── DOM helpers ──────────────────────────────────────────
  const $ = id => document.getElementById(id);
  const on = (el, ev, fn) => el && el.addEventListener(ev, fn);

  // ── Search Overlay ──────────────────────────────────────
  const searchOverlay = $('search-overlay');
  const searchInput   = $('search-input');

  function openSearch() {
    searchOverlay && searchOverlay.classList.add('open');
    setTimeout(() => searchInput && searchInput.focus(), 100);
  }
  function closeSearch() {
    searchOverlay && searchOverlay.classList.remove('open');
  }

  on($('search-open-btn'),        'click', openSearch);
  on($('search-open-btn-mobile'), 'click', openSearch);
  on($('search-close-btn'),       'click', closeSearch);

  // ESC key closes search
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      if (searchOverlay && searchOverlay.classList.contains('open')) closeSearch();
    }
  });

  // Quick search tags — set value AND submit the form
  document.querySelectorAll('[data-quick-search]').forEach(btn => {
    on(btn, 'click', () => {
      if (searchInput) {
        searchInput.value = btn.dataset.quickSearch;
        const form = searchInput.closest('form');
        if (form) form.submit();
      }
    });
  });

  // ── Mobile Menu ──────────────────────────────────────────
  const mobileMenu   = $('mobile-menu');
  const menuOpenBtn  = $('menu-open-btn');
  const menuCloseBtn = $('menu-close-btn');

  function openMenu() {
    mobileMenu && mobileMenu.classList.add('open');
    menuOpenBtn && menuOpenBtn.classList.add('hidden');
    menuCloseBtn && menuCloseBtn.classList.remove('hidden');
  }
  function closeMenu() {
    mobileMenu && mobileMenu.classList.remove('open');
    menuOpenBtn && menuOpenBtn.classList.remove('hidden');
    menuCloseBtn && menuCloseBtn.classList.add('hidden');
  }

  on($('menu-toggle-btn'), 'click', () => {
    mobileMenu && mobileMenu.classList.contains('open') ? closeMenu() : openMenu();
  });

  // ── Cart Drawer ──────────────────────────────────────────
  const cartDrawer   = $('cart-drawer');
  const cartBackdrop = $('cart-backdrop');

  function getActiveCartTab() {
    const activeBtn = document.querySelector('[data-cart-tab].border-primary-dark');
    return activeBtn ? activeBtn.dataset.cartTab : 'cart';
  }

  function syncCartFooterVisibility(tab) {
    const footer = $('cart-footer-panel');
    if (!footer) return;
    if (tab !== 'cart') {
      footer.classList.add('hidden');
      return;
    }
    if (footer.dataset.hasItems === 'true') {
      footer.classList.remove('hidden');
    }
  }

  function openCart() {
    cartDrawer   && cartDrawer.classList.add('open');
    cartBackdrop && cartBackdrop.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
    refreshCartDrawer();
    renderWishlistDrawer();
    syncCartFooterVisibility(getActiveCartTab());
  }
  function closeCart() {
    cartDrawer   && cartDrawer.classList.remove('open');
    cartBackdrop && cartBackdrop.classList.add('hidden');
    document.body.style.overflow = '';
  }

  // Listen for cart-updated (dispatched by product.js and quick-add handlers)
  document.addEventListener('recoco:cart-updated', () => {
    refreshCartDrawer();
  });

  // Listen for open-cart (dispatched by product.js after successful add)
  document.addEventListener('recoco:open-cart', () => {
    openCart();
  });

  document.querySelectorAll('[data-open-cart]').forEach(btn => on(btn, 'click', openCart));
  on($('cart-close-btn'), 'click', closeCart);
  on(cartBackdrop, 'click', closeCart);

  // Cart tabs
  document.querySelectorAll('[data-cart-tab]').forEach(btn => {
    on(btn, 'click', () => {
      const tab = btn.dataset.cartTab;
      document.querySelectorAll('[data-cart-tab]').forEach(b => {
        b.classList.remove('border-b-2', 'border-primary-dark', 'text-primary-dark');
        b.classList.add('text-text-light');
      });
      btn.classList.add('border-b-2', 'border-primary-dark', 'text-primary-dark');
      btn.classList.remove('text-text-light');

      document.querySelectorAll('[data-cart-panel]').forEach(p => p.classList.add('hidden'));
      const panel = document.querySelector('[data-cart-panel="' + tab + '"]');
      panel && panel.classList.remove('hidden');
      if (tab === 'wishlist') renderWishlistDrawer();
      syncCartFooterVisibility(tab);
    });
  });

  // ── Cart: event delegation for qty/remove (works after re-render) ──
  cartDrawer && cartDrawer.addEventListener('click', async (e) => {
    const wishlistCartBtn  = e.target.closest('[data-wishlist-add-to-cart]');
    const wishlistRemoveBtn = e.target.closest('[data-remove-wishlist]');
    const upBtn    = e.target.closest('[data-cart-qty-up]');
    const downBtn  = e.target.closest('[data-cart-qty-down]');
    const removeBtn = e.target.closest('[data-remove-item]');

    if (wishlistCartBtn) {
      const variantId = parseInt(wishlistCartBtn.dataset.wishlistAddToCart, 10);
      if (!variantId || wishlistCartBtn.disabled) return;
      wishlistCartBtn.disabled = true;
      try {
        const res = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body:    JSON.stringify({ id: variantId, quantity: 1 }),
        });
        if (res.ok) {
          wishlistCartBtn.textContent = 'Added!';
          document.querySelector('[data-cart-tab="cart"]')?.click();
          refreshCartDrawer();
        } else {
          wishlistCartBtn.disabled = false;
        }
      } catch (err) {
        console.error('[Wishlist Add to Cart]', err);
        wishlistCartBtn.disabled = false;
      }
    } else if (wishlistRemoveBtn) {
      const items = getWishlist().filter(item => item.id !== wishlistRemoveBtn.dataset.removeWishlist);
      saveWishlist(items);
      updateWishlistBtns();
      renderWishlistDrawer();
    } else if (upBtn) {
      const key   = upBtn.dataset.cartQtyUp;
      const bundleId = upBtn.dataset.bundleId;
      const qtyEl = cartDrawer.querySelector('[data-qty-display="' + key + '"]');
      if (qtyEl) await updateCartItem(key, parseInt(qtyEl.textContent) + 1, bundleId);
    } else if (downBtn) {
      const key   = downBtn.dataset.cartQtyDown;
      const bundleId = downBtn.dataset.bundleId;
      const qtyEl = cartDrawer.querySelector('[data-qty-display="' + key + '"]');
      if (qtyEl) await updateCartItem(key, Math.max(1, parseInt(qtyEl.textContent) - 1), bundleId);
    } else if (removeBtn) {
      const bundleId = removeBtn.dataset.bundleId;
      await updateCartItem(removeBtn.dataset.removeItem, 0, bundleId);
    }
  });

  async function updateCartItem(key, qty, bundleId) {
    try {
      let updates = {};
      
      if (bundleId) {
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        cart.items.forEach(item => {
          if (item.properties && item.properties['_bundle_id'] === bundleId) {
            updates[item.key] = qty;
          }
        });
      } else {
        updates[key] = qty;
      }

      const res = await fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ updates }),
      });
      if (res.ok) refreshCartDrawer();
    } catch (e) { console.error('[Cart change]', e); }
  }

  // ── Money formatter (consistent with Shopify INR format) ──
  function formatMoney(paise) {
    const rupees = paise / 100;
    return '\u20B9' + rupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // ── Cart refresh: fetch live data and re-render drawer ───
  function cartItemHTML(item) {
    let discountPct = 0;
    if (item.quantity >= 5) {
      discountPct = 0.15;
    } else if (item.quantity >= 3) {
      discountPct = 0.10;
    }
    
    let currentPrice = item.price;
    let originalPrice = item.price;
    if (discountPct > 0) {
      currentPrice = Math.round(originalPrice * (1 - discountPct));
    }

    const price        = formatMoney(currentPrice);
    
    let compareHTML = '';
    if (discountPct > 0) {
      compareHTML = '<p class="text-[10px] text-text-light line-through">' + formatMoney(originalPrice) + '</p>' + 
                    '<p class="text-[10px] font-bold text-primary-tan uppercase mt-0.5">Save ' + (discountPct * 100) + '%</p>';
    } else if (item.compare_at_price > item.price) {
      compareHTML = '<p class="text-[10px] text-text-light line-through">' + formatMoney(item.compare_at_price) + '</p>';
    }

    const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
      ? '<p class="text-[10px] text-text-light">' + item.variant_title + '</p>'
      : '';
      
    let propertiesHTML = '';
    if (item.properties) {
      for (const [key, value] of Object.entries(item.properties)) {
        if (key.startsWith('_')) continue;
        if (!value) continue;
        propertiesHTML += '<p class="text-[10px] text-text-light"><span class="font-medium">' + key + ':</span> ' + value + '</p>';
      }
      if (propertiesHTML) {
        propertiesHTML = '<div class="mt-1">' + propertiesHTML + '</div>';
      }
    }

    const imgTag = item.image
      ? '<img src="' + item.image + '" alt="' + item.product_title + '" class="w-full h-full object-cover">'
      : '';

    const isAddon = item.properties && item.properties['_is_addon'] === 'true';
    const bundleId = item.properties && item.properties['_bundle_id'] ? item.properties['_bundle_id'] : '';

    return (
      '<div class="flex gap-3 items-center">' +
        '<a href="' + item.url + '" class="w-14 h-14 bg-gray-300 shrink-0 overflow-hidden">' + imgTag + '</a>' +
        '<div class="flex-1 min-w-0">' +
          '<p class="text-xs font-medium text-text-dark leading-snug truncate">' + item.product_title + '</p>' +
          variantTitle + propertiesHTML +
          (isAddon ? '' : '<div class="flex items-center border border-gray-200 w-fit mt-1">' +
            '<button data-cart-qty-down="' + item.key + '" data-bundle-id="' + bundleId + '" class="px-2 py-1 text-xs hover:bg-gray-50" aria-label="Decrease quantity">\u2212</button>' +
            '<span class="px-2 text-xs" data-qty-display="' + item.key + '">' + item.quantity + '</span>' +
            '<button data-cart-qty-up="' + item.key + '" data-bundle-id="' + bundleId + '" class="px-2 py-1 text-xs hover:bg-gray-50" aria-label="Increase quantity">+</button>' +
          '</div>') +
        '</div>' +
        '<div class="text-right shrink-0">' +
          '<p class="text-xs font-semibold text-text-dark">' + price + '</p>' +
          compareHTML +
        '</div>' +
        (isAddon ? '' : '<button data-remove-item="' + item.key + '" data-bundle-id="' + bundleId + '" class="text-gray-400 hover:text-red-400 transition-colors ml-1" aria-label="Remove item">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
        '</button>') +
      '</div>'
    );
  }

  async function refreshCartDrawer() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();

      // Update all count badges
      document.querySelectorAll('[data-cart-count]').forEach(el => el.textContent = cart.item_count);

      const itemsPanel = $('cart-items-panel');
      const footer     = $('cart-footer-panel');
      const subtotal   = $('cart-subtotal');
      
      let cartSubtotal = 0;
      let totalDiscount = 0;
      let totalQty = 0;
      
      // Calculate subtotal with our custom discounts
      cart.items.forEach(item => {
        let discountPct = 0;
        if (item.quantity >= 5) {
          discountPct = 0.15;
        } else if (item.quantity >= 3) {
          discountPct = 0.10;
        }
        const discountedPrice = Math.round(item.price * (1 - discountPct));
        cartSubtotal += discountedPrice * item.quantity;
        totalDiscount += (item.price - discountedPrice) * item.quantity;
        totalQty += item.quantity;
      });

      if (cart.item_count === 0) {
        if (itemsPanel) itemsPanel.innerHTML =
          '<div class="flex-1 flex flex-col items-center justify-center text-center py-10 text-text-light">' +
            '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="mb-3 opacity-30"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>' +
            '<p class="text-sm">Your cart is empty</p>' +
          '</div>';
        if (footer) {
          footer.dataset.hasItems = 'false';
          footer.classList.add('hidden');
        }
      } else {
        let bannerHTML = '';
        if (totalQty >= 5) {
          bannerHTML = '<div class="mb-4 p-2 bg-green-50 text-green-800 border border-green-200 rounded-lg text-xs text-center">🎉 <span class="font-bold">Best Offer!</span> You unlocked <span class="font-bold">15% OFF</span></div>';
        } else if (totalQty >= 3) {
          bannerHTML = '<div class="mb-4 p-2 bg-green-50 text-green-800 border border-green-200 rounded-lg text-xs text-center">🎊 You unlocked <span class="font-bold">10% OFF</span>.<br>Add ' + (5 - totalQty) + ' more for 15% OFF!</div>';
        } else {
          bannerHTML = '<div class="mb-4 p-2 bg-orange-50 text-orange-800 border border-orange-200 rounded-lg text-xs text-center">Buy ' + (3 - totalQty) + ' more item' + ((3 - totalQty) > 1 ? 's' : '') + ' to save <span class="font-bold">10%</span>!</div>';
        }
        
        if (itemsPanel) itemsPanel.innerHTML = bannerHTML + cart.items.map(cartItemHTML).join('');
        
        if (subtotal) {
          if (totalDiscount > 0) {
            subtotal.innerHTML = `<div class="flex flex-col items-end"><span class="text-sm font-semibold text-text-dark">${formatMoney(cartSubtotal)}</span><span class="text-[10px] text-primary-tan font-semibold uppercase tracking-wide">You saved ${formatMoney(totalDiscount)}!</span></div>`;
          } else {
            subtotal.textContent = formatMoney(cartSubtotal);
          }
        }
        
        if (footer) footer.dataset.hasItems = 'true';
      }

      syncCartFooterVisibility(getActiveCartTab());
    } catch (e) { console.error('[Cart refresh]', e); }
  }

  // ── Global Add to Cart (handles cards, home sections — NOT the product page button) ──
  document.querySelectorAll('[data-add-to-cart], .add-to-cart-btn').forEach(btn => {
    // product.js handles the main product page button
    if (btn.id === 'add-to-cart-btn') return;

    on(btn, 'click', async () => {
      const variantId = parseInt(btn.dataset.addToCart || btn.dataset.variantId, 10);
      const qty       = parseInt(btn.dataset.qty || '1', 10);
      const origHTML  = btn.innerHTML;
      if (!variantId || btn.disabled) return;

      btn.innerHTML = '<span>Adding...</span>';
      btn.disabled  = true;

      try {
        const res = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body:    JSON.stringify({ id: variantId, quantity: qty }),
        });

        if (!res.ok) throw new Error('Failed to add item to cart');

        btn.innerHTML = '<span>Added!</span>';
        refreshCartDrawer();
        openCart();
        setTimeout(() => {
          btn.innerHTML = origHTML;
          btn.disabled  = false;
        }, 2000);
      } catch (e) {
        console.error('[Quick Add to Cart]', e);
        btn.innerHTML = origHTML;
        btn.disabled  = false;
      }
    });
  });

  // ── Wishlist (localStorage) ──────────────────────────────
  const WL_KEY = 'recoco_wishlist';

  function normalizeWishlistItem(item) {
    if (!item) return null;
    if (typeof item === 'string' || typeof item === 'number') return { id: String(item) };
    if (typeof item !== 'object' || !item.id) return null;
    return {
      id:           String(item.id),
      title:        item.title        || '',
      url:          item.url          || '',
      image:        item.image        || '',
      price:        item.price        || '',
      comparePrice: item.comparePrice || '',
      variantId:    item.variantId    ? String(item.variantId) : '',
    };
  }

  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(WL_KEY) || '[]')
        .map(normalizeWishlistItem)
        .filter(Boolean);
    } catch { return []; }
  }

  function saveWishlist(items) {
    localStorage.setItem(WL_KEY, JSON.stringify(items));
  }

  function buildWishlistItem(btn) {
    return normalizeWishlistItem({
      id:           btn.dataset.wishlistToggle,
      title:        btn.dataset.productTitle,
      url:          btn.dataset.productUrl,
      image:        btn.dataset.productImage,
      price:        btn.dataset.productPrice,
      comparePrice: btn.dataset.productComparePrice,
      variantId:    btn.dataset.productVariantId,
    });
  }

  // Global toggleWishlist — called from product-card onclick handlers
  window.toggleWishlist = function (productId, btnEl) {
    const id    = String(productId);
    const items = getWishlist();
    const idx   = items.findIndex(item => item.id === id);

    if (idx > -1) {
      items.splice(idx, 1);
    } else {
      // Build a minimal item from the button's data attributes
      const item = normalizeWishlistItem({
        id:        id,
        title:     btnEl && btnEl.dataset.productTitle     ? btnEl.dataset.productTitle     : '',
        url:       btnEl && btnEl.dataset.productUrl       ? btnEl.dataset.productUrl       : '',
        image:     btnEl && btnEl.dataset.productImage     ? btnEl.dataset.productImage     : '',
        price:     btnEl && btnEl.dataset.productPrice     ? btnEl.dataset.productPrice     : '',
        variantId: btnEl && btnEl.dataset.productVariantId ? btnEl.dataset.productVariantId : '',
      });
      if (item) items.push(item);
    }

    saveWishlist(items);
    updateWishlistBtns();
    renderWishlistDrawer();
  };

  function renderWishlistDrawer() {
    const container = $('wishlist-drawer-items');
    if (!container) return;
    const items = getWishlist();

    if (!items.length) {
      container.innerHTML =
        '<div class="flex-1 flex flex-col items-center justify-center text-center py-10 text-text-light">' +
          '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="mb-3 opacity-30"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>' +
          '<p class="text-sm">Your wishlist is empty</p>' +
        '</div>';
      return;
    }

    container.innerHTML = items.map(item => {
      const title   = item.title || 'Saved item';
      const href    = item.url   || '/pages/wishlist';
      const price   = item.price
        ? '<p class="text-xs font-semibold text-text-dark">\u20B9' + item.price + '</p>'
        : '';
      const compare = item.comparePrice
        ? '<p class="text-[10px] text-text-light line-through">\u20B9' + item.comparePrice + '</p>'
        : '';
      const image   = item.image
        ? '<img src="' + item.image + '" alt="' + title + '" class="w-full h-full object-cover">'
        : '';
      const action  = item.variantId
        ? '<button data-wishlist-add-to-cart="' + item.variantId + '" class="px-3 py-1.5 text-[11px] font-medium rounded-full bg-primary-dark text-white hover:bg-opacity-90 transition-colors">Add to Cart</button>'
        : '<a href="' + href + '" class="px-3 py-1.5 text-[11px] font-medium rounded-full border border-border-light text-text-dark hover:border-primary-dark transition-colors">View Product</a>';

      return (
        '<div class="flex gap-3 items-center">' +
          '<a href="' + href + '" class="w-14 h-14 bg-gray-300 shrink-0 overflow-hidden rounded-md">' + image + '</a>' +
          '<div class="flex-1 min-w-0">' +
            '<a href="' + href + '" class="text-xs font-medium text-text-dark leading-snug line-clamp-2 hover:text-primary-dark transition-colors">' + title + '</a>' +
            '<div class="mt-1">' + price + compare + '</div>' +
            '<div class="mt-2 flex items-center gap-2">' + action + '</div>' +
          '</div>' +
          '<button data-remove-wishlist="' + item.id + '" class="text-gray-400 hover:text-red-400 transition-colors ml-1" aria-label="Remove from wishlist">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
          '</button>' +
        '</div>'
      );
    }).join('');
  }

  function updateWishlistBtns() {
    const ids = getWishlist().map(item => item.id);
    // Update [data-wishlist-toggle] buttons (product page)
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      const id = btn.dataset.wishlistToggle;
      if (ids.includes(id)) {
        btn.classList.add('text-primary-tan', 'border-primary-tan');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('text-primary-tan', 'border-primary-tan');
        btn.setAttribute('aria-pressed', 'false');
      }
    });
    // Update product card wishlist icon buttons
    document.querySelectorAll('[data-product-id].wishlist-btn, .wishlist-btn[data-product-id]').forEach(btn => {
      const id   = btn.dataset.productId;
      const icon = btn.querySelector('.wishlist-icon');
      if (ids.includes(String(id))) {
        icon && (icon.style.fill = 'currentColor');
        btn.classList.add('text-primary-tan');
      } else {
        icon && (icon.style.fill = 'none');
        btn.classList.remove('text-primary-tan');
      }
    });
    // Update count badges
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = ids.length;
    });
  }

  // [data-wishlist-toggle] buttons (product page style)
  document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
    on(btn, 'click', () => {
      const id    = btn.dataset.wishlistToggle;
      const items = getWishlist();
      const idx   = items.findIndex(item => item.id === id);
      if (idx > -1) {
        items.splice(idx, 1);
      } else {
        const item = buildWishlistItem(btn);
        if (item) items.push(item);
      }
      saveWishlist(items);
      updateWishlistBtns();
      renderWishlistDrawer();
    });
  });

  updateWishlistBtns();
  renderWishlistDrawer();

  // ── Team carousel (About page) ───────────────────────────
  const carousel = $('team-carousel');
  if (carousel) {
    const items   = carousel.querySelectorAll('[data-team-item]');
    let start     = 0;
    const visible = () => window.innerWidth < 640 ? 3 : 5;

    function renderCarousel() {
      items.forEach((item, i) => {
        item.classList.toggle('hidden', i < start || i >= start + visible());
      });
    }
    renderCarousel();

    on($('team-prev'), 'click', () => { start = Math.max(0, start - 1); renderCarousel(); });
    on($('team-next'), 'click', () => { start = Math.min(items.length - visible(), start + 1); renderCarousel(); });
    window.addEventListener('resize', renderCarousel);
  }

  // ── Hide header on scroll down, show on scroll up ────────
  const header = document.querySelector('header');
  let lastScrollTop = 0;
  const SCROLL_THRESHOLD = 50;

  window.addEventListener('scroll', () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    if (Math.abs(scrollTop - lastScrollTop) < SCROLL_THRESHOLD) return;
    if (scrollTop > lastScrollTop && scrollTop > 100) {
      header && header.classList.add('hide-header');
    } else {
      header && header.classList.remove('hide-header');
    }
    lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
  }, { passive: true });

})();
